import type { ConsentState } from "../core/consent";
import type { CookieConfig } from "../core/config";

type ConsentAccessor = () => ConsentState | null;

let installed = false;
let cookieCatalog: CookieConfig[] = [];
let getConsent: ConsentAccessor = () => null;

/** Common localStorage keys to clear when analytics/marketing categories are denied */
const LOCALSTORAGE_KEYS_BY_CATEGORY: Record<string, string[]> = {
  analytics: ["_ga", "_gid", "_gat", "ga", "gid"],
  marketing: ["_fbp", "_fbc", "fbp", "fbc", "_gcl_au", "li_sugr", "li_at"],
};

function getCookieName(cookieAssignment: string): string {
  const firstPart = cookieAssignment.split(";")[0] || "";
  const [name] = firstPart.split("=");
  return (name || "").trim();
}

function resolveCookieCategory(cookieName: string): string | null {
  const lower = cookieName.toLowerCase();
  const direct = cookieCatalog.find((cookie) => cookie.cookieName.toLowerCase() === lower);
  if (!direct) return null;
  return direct.category;
}

function isCookieAllowed(cookieName: string): boolean {
  const current = getConsent();
  if (!current) return false;

  const matched = cookieCatalog.find((cookie) => cookie.cookieName.toLowerCase() === cookieName.toLowerCase());
  if (!matched) return true;

  const categoryAllowed = current.categories[matched.category] ?? false;
  const cookieAllowed = current.cookies[matched.key];
  return categoryAllowed && (cookieAllowed ?? true);
}

/**
 * Clear localStorage keys for denied categories (analytics, marketing).
 * Called when user revokes consent or changes to reject_all.
 */
export function clearDeniedLocalStorage(deniedCategories: string[]): void {
  try {
    const keysToRemove = new Set<string>();
    for (const cat of deniedCategories) {
      const keys = LOCALSTORAGE_KEYS_BY_CATEGORY[cat];
      if (keys) keys.forEach((k) => keysToRemove.add(k));
    }
    // Also remove keys that start with _ga_ (Google Analytics) or AMP_
    const allKeys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i)).filter(
      (k): k is string => !!k
    );
    for (const key of allKeys) {
      if (key.startsWith("_ga_") || key.startsWith("AMP_")) keysToRemove.add(key);
    }
    for (const key of keysToRemove) {
      try {
        localStorage.removeItem(key);
      } catch {
        /* best effort */
      }
    }
    if (keysToRemove.size > 0) {
      console.info(`[CMP] Cleared ${keysToRemove.size} localStorage key(s) for revoked categories.`);
    }
  } catch {
    /* localStorage might be unavailable */
  }
}

export function clearDeniedCookies(): void {
  const current = getConsent();
  if (!current) return;

  for (const cookie of cookieCatalog) {
    const categoryAllowed = current.categories[cookie.category] ?? false;
    const cookieAllowed = current.cookies[cookie.key];
    if (categoryAllowed && (cookieAllowed ?? true)) continue;

    const namesToDelete = [
      `${cookie.cookieName}=; path=/; max-age=0`,
      `${cookie.cookieName}=; domain=${cookie.cookieDomain}; path=/; max-age=0`,
    ];

    for (const statement of namesToDelete) {
      try {
        document.cookie = statement;
      } catch {
        // best effort
      }
    }
  }
}

export function installCookieWriteGuard(cookies: CookieConfig[] | undefined, consentAccessor: ConsentAccessor): void {
  cookieCatalog = cookies || [];
  getConsent = consentAccessor;

  if (installed) return;
  installed = true;

  const descriptor = Object.getOwnPropertyDescriptor(Document.prototype, "cookie");
  if (!descriptor?.set || !descriptor.get) return;

  Object.defineProperty(Document.prototype, "cookie", {
    configurable: true,
    enumerable: descriptor.enumerable ?? true,
    get: descriptor.get,
    set(value: string) {
      const cookieName = getCookieName(value);
      if (!cookieName) {
        descriptor.set!.call(document, value);
        return;
      }

      const category = resolveCookieCategory(cookieName);
      if (!category) {
        descriptor.set!.call(document, value);
        return;
      }

      if (!isCookieAllowed(cookieName)) {
        console.info(`[CMP] Cookie write blocked: ${cookieName} (${category})`);
        return;
      }

      descriptor.set!.call(document, value);
    },
  });
}
